import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Users } from "lucide-react";
import { http, fmtErr } from "../lib/api";
import { WholesalerSupportCard } from "../components/WholesalerSupportCard";

export default function WholesaleApply() {
  const location = useLocation();
  const refCode = (new URLSearchParams(location.search).get("ref") || "").toUpperCase();
  const [referrer, setReferrer] = useState(null);
  const [form, setForm] = useState({
    business_name: "", contact_name: "", email: "", phone: "",
    business_type: "gas_station", address: "", resale_cert: "", expected_volume: "", notes: "",
    referred_by_code: refCode,
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!refCode) return;
    http.get(`/referral/${refCode}`).then(({ data }) => setReferrer(data)).catch(() => {});
  }, [refCode]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await http.post("/wholesale/apply", form);
      setDone(true);
      toast.success("Application received!");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="wholesale-success">
        <div className="w-20 h-20 mx-auto bg-[#7CB342] flex items-center justify-center mb-6"><ShieldCheck className="text-white" size={40} /></div>
        <h1 className="font-heading text-5xl uppercase tracking-wide">Application received</h1>
        <p className="mt-4 text-[#5C5A56] text-lg">We review new wholesalers within 1-2 business days. You&apos;ll receive an email once approved with your buyer portal credentials.</p>
        <Link to="/" className="inline-block mt-8 border-2 border-[#1A1A1A] px-8 py-3 uppercase tracking-widest font-heading hover:bg-[#1A1A1A] hover:text-white">Back home</Link>
      </div>
    );
  }

  const input = "w-full bg-white border border-[#E5D8C1] px-4 py-3 focus:outline-none focus:border-[#D32F2F] transition-colors";

  return (
    <div className="bg-[#FFFDF8]" data-testid="wholesale-apply-root">
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-16">
        <div className="text-xs tracking-[0.3em] uppercase text-[#D32F2F] mb-3">Wholesale Application</div>
        <h1 className="font-heading text-6xl uppercase tracking-wide">Partner with us</h1>
        <p className="mt-4 text-lg text-[#5C5A56]">Tell us about your business. We approve most gas stations, vape/smoke shops, and party stores within 1-2 business days.</p>
        {referrer && (
          <div className="mt-6 bg-[#FFB300] text-[#1A1A1A] p-5 flex items-center gap-4" data-testid="wholesale-referrer-banner">
            <Users size={28} />
            <div>
              <div className="font-heading text-xl uppercase tracking-wide leading-none">Invited by {referrer.business_name}</div>
              <div className="text-sm mt-1">Your application will be fast-tracked — code <b>{referrer.code}</b> applied.</div>
            </div>
          </div>
        )}
        <form onSubmit={submit} className="mt-10 bg-[#FDF4E3] border border-[#E5D8C1] p-8 md:p-10 space-y-5">
          <div className="grid md:grid-cols-2 gap-5">
            <label className="block"><span className="text-xs uppercase tracking-widest text-[#1A1A1A]">Business name *</span>
              <input required value={form.business_name} onChange={(e) => setForm({...form, business_name: e.target.value})} className={input + " mt-2"} data-testid="wholesale-business-name" />
            </label>
            <label className="block"><span className="text-xs uppercase tracking-widest">Contact name *</span>
              <input required value={form.contact_name} onChange={(e) => setForm({...form, contact_name: e.target.value})} className={input + " mt-2"} data-testid="wholesale-contact-name" />
            </label>
            <label className="block"><span className="text-xs uppercase tracking-widest">Business email *</span>
              <input required type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className={input + " mt-2"} data-testid="wholesale-email" />
            </label>
            <label className="block"><span className="text-xs uppercase tracking-widest">Phone *</span>
              <input required value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className={input + " mt-2"} data-testid="wholesale-phone" />
            </label>
          </div>
          <label className="block"><span className="text-xs uppercase tracking-widest">Business type *</span>
            <select value={form.business_type} onChange={(e) => setForm({...form, business_type: e.target.value})} className={input + " mt-2"} data-testid="wholesale-type">
              <option value="gas_station">Gas station / c-store</option>
              <option value="vape_shop">Vape / smoke shop</option>
              <option value="grocery">Grocery / bodega</option>
              <option value="party_store">Party / event store</option>
              <option value="other">Other</option>
            </select>
          </label>
          <label className="block"><span className="text-xs uppercase tracking-widest">Business address *</span>
            <input required value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} className={input + " mt-2"} data-testid="wholesale-address" />
          </label>
          <div className="grid md:grid-cols-2 gap-5">
            <label className="block"><span className="text-xs uppercase tracking-widest">Resale certificate # (optional)</span>
              <input value={form.resale_cert} onChange={(e) => setForm({...form, resale_cert: e.target.value})} className={input + " mt-2"} data-testid="wholesale-resale" />
            </label>
            <label className="block"><span className="text-xs uppercase tracking-widest">Expected monthly volume</span>
              <input value={form.expected_volume} onChange={(e) => setForm({...form, expected_volume: e.target.value})} placeholder="e.g. 20 cases/mo" className={input + " mt-2"} data-testid="wholesale-volume" />
            </label>
          </div>
          <label className="block"><span className="text-xs uppercase tracking-widest">Notes</span>
            <textarea rows={4} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className={input + " mt-2 resize-none"} data-testid="wholesale-notes" />
          </label>
          <button type="submit" disabled={loading} className="w-full bg-[#D32F2F] text-white hover:bg-[#B71C1C] py-4 uppercase tracking-widest font-heading text-lg transition-transform hover:-translate-y-1 disabled:opacity-60 flex items-center justify-center gap-2" data-testid="wholesale-submit">
            {loading && <Loader2 className="animate-spin" size={18} />} Submit application
          </button>
        </form>

        <div className="mt-16">
          <WholesalerSupportCard />
        </div>
      </div>
    </div>
  );
}
